import * as React from 'react';
import { useRef } from 'react';
import { useListBox, useOption } from '@react-aria/listbox';
import { useListState } from '@react-stately/list';
import { mergeProps } from '@react-aria/utils';
import type { AriaListBoxProps } from '@react-aria/listbox';
import type { ListState } from '@react-stately/list';
import type { Selection } from '@react-types/shared';
import classnames from 'classnames';
import { AtollonCheckbox, CheckboxVariant } from '../../../ui/Checkbox';
import Visibility from "@spectrum-icons/workflow/Visibility";
import Filter from "@spectrum-icons/workflow/Filter";
import styles from './ColumnsList.module.css';
import Rail from '@spectrum-icons/workflow/Rail';

export interface IColumnItem {
  itemname: string;
  id: string;
  label: string;
  isVisible: boolean;
  isFilter?: boolean;
  type?: string; // AOT field type: "text", "numeric", "date", "currency", etc.
}

export interface IColumnSection {
  id: string;
  label: string;
  items: IColumnItem[];
}

export interface IColumnsListProps extends Omit<AriaListBoxProps<IColumnItem>, 'children' | 'onSelectionChange' | 'autoFocus' | 'selectedKeys'> {
  sections: IColumnSection[];
  selectedKeys?: Set<string> | 'all';
  onSelectionChange?: (keys: Set<string> | 'all') => void;
  onVisibilityChange?: (itemId: string, isVisible: boolean) => void;
  onFilterChange?: (itemId: string, isFilter: boolean) => void;
  onReorder?: (reorderedItems: IColumnItem[]) => void;
  autoFocus?: boolean;
  isGroup?: boolean;
  isAllowDnD?: boolean;
}

interface OptionProps {
  item: IColumnItem;
  state: ListState<IColumnItem>;
  isIndeterminate?: boolean;
  onVisibilityChange?: (itemId: string, isVisible: boolean) => void;
  onFilterChange?: (itemId: string, isFilter: boolean) => void;
  isAllowDnD?: boolean;
  isGroup?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (itemId: string) => void;
  onDragOver?: (itemId: string, e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDragLeave?: () => void;
}

function Option({ item, state, isIndeterminate, onVisibilityChange, onFilterChange, isAllowDnD, isGroup, isDragging, isDragOver, onDragStart, onDragOver: handleDragOver, onDragEnd, onDragLeave }: OptionProps) {
  const ref = useRef<HTMLLIElement>(null);
  const dragHandleRef = React.useRef<HTMLDivElement>(null);
  const { optionProps, isSelected, isFocused, isDisabled } = useOption(
    { key: item.id },
    state,
    ref
  );

  // Check if this is a filter-only column (type starts with 'filter.')
  const isFilterOnlyColumn = React.useMemo(() => {
    return item.type?.startsWith('filter.') === true;
  }, [item.type]);
  
  // Check if drag and drop should be enabled
  const canDrag = isAllowDnD === true && isGroup === false;
  
  // Create drag handle event handlers (using React events like FilterViewsComponent)
  const handleDragHandleDragStart = React.useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    if (onDragStart) {
      onDragStart(item.id);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", item.id);
    }
  }, [item.id, onDragStart]);
  
  const handleDragHandleDragEnd = React.useCallback(() => {
    if (onDragEnd) {
      onDragEnd();
    }
  }, [item.id, onDragEnd]);
  
  const handleDragHandleMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  
  const handleDragHandlePointerDown = React.useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  // Handle checkbox selection change
  const handleCheckboxChange = React.useCallback((checked: boolean) => {
    if (isDisabled) return;
    
    // Get current selection
    const currentSelection = state.selectionManager.selectedKeys;
    const newSelection = new Set(currentSelection);
    
    if (checked) {
      newSelection.add(item.id);
    } else {
      newSelection.delete(item.id);
    }
    
    // Update selection using replace method
    state.selectionManager.setSelectedKeys(newSelection);
  }, [item.id, state.selectionManager, isDisabled]);

  const dragHandlers = canDrag ? {
    onDragOver: (e: React.DragEvent) => {
      if (handleDragOver) {
        handleDragOver(item.id, e);
      }
    },
    onDragLeave: onDragLeave,
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onDragEnd) {
        onDragEnd();
      }
    }
  } : undefined;

  return (
    <li
      {...mergeProps(optionProps, dragHandlers || {})}
      ref={ref}
      className={classnames(styles.item, {
        [styles.isSelected]: isSelected,
        [styles.isFocused]: isFocused,
        [styles.isDisabled]: isDisabled,
        [styles.itemDragging]: isDragging,
        [styles.itemDragOver]: isDragOver,
      })}
    >
      <div className={styles.itemContent}>
        <div className={styles.iconGroup}>
          {canDrag && (
            <div
              ref={dragHandleRef}
              className={styles.dragHandle}
              draggable={true}
              onDragStart={handleDragHandleDragStart}
              onDragEnd={handleDragHandleDragEnd}
              onMouseDown={handleDragHandleMouseDown}
              onPointerDown={handleDragHandlePointerDown}
            >
              <Rail size="S" />
            </div>
          )}
          {!isFilterOnlyColumn && (
            <button
              type="button"
              className={classnames(styles.visibilityIcon, {
                [styles.isVisible]: item.isVisible,
                [styles.isHidden]: !item.isVisible,
              })} 
              style={{
                color: item.isVisible ? 'var(--primary-50)' : 'var(--neutral-60)',
                '--icon-color': item.isVisible ? 'var(--primary-50)' : 'var(--neutral-60)',
              } as React.CSSProperties & { '--icon-color': string }}
              aria-label={item.isVisible ? 'Hide column' : 'Show column'}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled && onVisibilityChange) {
                  onVisibilityChange(item.id, !item.isVisible);
                }
              }}
              disabled={isDisabled}
            >
              <Visibility />
            </button>
          )}
          <button
            type="button"
            className={classnames(styles.filterIcon, {
              [styles.isFilter]: item.isFilter === true,
              [styles.isNotFilter]: item.isFilter !== true,
            })} 
            style={{
              color: item.isFilter === true ? 'var(--primary-50)' : 'var(--neutral-60)',
              '--icon-color': item.isFilter === true ? 'var(--primary-50)' : 'var(--neutral-60)',
            } as React.CSSProperties & { '--icon-color': string }}
            aria-label={item.isFilter === true ? 'Remove filter' : 'Apply filter'}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled && onFilterChange) {
                onFilterChange(item.id, !(item.isFilter === true));
              }
            }}
            disabled={isDisabled}
          >
            <Filter />
          </button>
        </div>
        <span className={styles.itemLabel}>{item.label}</span>
        <div 
          className={styles.checkboxWrapper}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled) {
              // Toggle selection when checkbox wrapper is clicked
              handleCheckboxChange(!isSelected);
            }
          }}
        >
          <AtollonCheckbox
            variant={CheckboxVariant.EMPHASIZED}
            isSelected={isSelected}
            onChange={handleCheckboxChange}
            isDisabled={isDisabled}
            aria-label={`Select ${item.label}`}
          />
        </div>
      </div>
    </li>
  );
}

export const ColumnsList = React.forwardRef<HTMLUListElement, IColumnsListProps>(
  (props, forwardedRef) => {
    const { sections = [], selectedKeys, onSelectionChange, onVisibilityChange, onFilterChange, onReorder, isGroup = true, isAllowDnD = false, ...otherProps } = props;
    
    // Drag and drop state
    const [draggedKey, setDraggedKey] = React.useState<string | null>(null);
    const [dragOverKey, setDragOverKey] = React.useState<string | null>(null);
    const [reorderedItems, setReorderedItems] = React.useState<IColumnItem[]>([]);
    // Track if drop has occurred to prevent double processing
    const dropOccurredRef = React.useRef<boolean>(false);
    
    // Flatten items from all sections for React Aria state management
    const allItems = React.useMemo(() => {
      if (!sections || sections.length === 0) {
        return [];
      }
      return sections.flatMap((section) => section.items || []);
    }, [sections]);
    
    // Sync reorderedItems with sections when drag and drop is enabled
    React.useEffect(() => {
      if (isAllowDnD && !isGroup) {
        setReorderedItems(allItems);
      }
    }, [allItems, isAllowDnD, isGroup]);

    // Compute selectedKeys value for state initialization
    const computedSelectedKeys = React.useMemo(() => {
      if (selectedKeys === undefined) {
        return undefined;
      }
      if (selectedKeys === 'all') {
        return 'all';
      }
      if (selectedKeys instanceof Set) {
        return selectedKeys;
      }
      return new Set<string>();
    }, [selectedKeys]);

    // Use reorderedItems when drag and drop is enabled, otherwise use allItems
    const itemsForState = React.useMemo(() => {
      if (isAllowDnD && !isGroup && reorderedItems.length > 0) {
        return reorderedItems;
      }
      return allItems;
    }, [allItems, reorderedItems, isAllowDnD, isGroup]);

    const state = useListState({
      ...otherProps,
      items: itemsForState,
      ...(computedSelectedKeys !== undefined 
        ? { selectedKeys: computedSelectedKeys } 
        : {}),
      onSelectionChange: (keys: Selection) => {
        if (keys === 'all') {
          onSelectionChange?.('all');
          return;
        }
        const selectedSet = new Set<string>(Array.from(keys) as string[]);
        onSelectionChange?.(selectedSet);
      },
      selectionMode: 'multiple',
    });
    
    // Drag and drop handlers
    const handleDragStart = React.useCallback((itemId: string) => {
      if (!isAllowDnD || isGroup) return;
      dropOccurredRef.current = false; // Reset drop flag on new drag
      setDraggedKey(itemId);
    }, [isAllowDnD, isGroup]);
    
    const handleDragOver = React.useCallback((itemId: string, e: React.DragEvent) => {
      if (!isAllowDnD || isGroup || !draggedKey || draggedKey === itemId) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOverKey(itemId);
    }, [isAllowDnD, isGroup, draggedKey]);
    
    const handleDragEnd = React.useCallback(() => {
      // If drop already occurred (flag set from previous call), skip processing to prevent double call
      if (dropOccurredRef.current) {
        // Reset drag states
        setTimeout(() => {
          setDraggedKey(null);
          setDragOverKey(null);
          dropOccurredRef.current = false;
        }, 0);
        return;
      }
      
      if (!isAllowDnD || isGroup || !draggedKey) {
        setDraggedKey(null);
        setDragOverKey(null);
        return;
      }
      
      if (draggedKey && dragOverKey && draggedKey !== dragOverKey) {
        const draggedIndex = reorderedItems.findIndex(item => item.id === draggedKey);
        const dropIndex = reorderedItems.findIndex(item => item.id === dragOverKey);
        if (draggedIndex !== -1 && dropIndex !== -1) {
          const newItems = [...reorderedItems];
          const [removed] = newItems.splice(draggedIndex, 1);
          newItems.splice(dropIndex, 0, removed);
          setReorderedItems(newItems);
          
          // Mark that drop occurred to prevent subsequent handleDragHandleDragEnd from processing
          dropOccurredRef.current = true;
          
          // Notify parent of reordered items
          if (onReorder) {
            onReorder(newItems);
          }
        }
      }
      
      // Reset drag states - use setTimeout to ensure hover state resets properly
      setTimeout(() => {
        setDraggedKey(null);
        setDragOverKey(null);
        dropOccurredRef.current = false;
      }, 0);
    }, [isAllowDnD, isGroup, draggedKey, dragOverKey, reorderedItems, onReorder]);
    
    const handleDragLeave = React.useCallback(() => {
      setDragOverKey(null);
    }, []);

    const listBoxRef = useRef<HTMLUListElement>(null);
    const { listBoxProps } = useListBox(
      {
        ...otherProps,
        'aria-label': otherProps['aria-label'] || 'Columns List',
        autoFocus: props.autoFocus ? 'first' : undefined,
      },
      state,
      listBoxRef
    );

    // Merge forwarded ref with internal ref
    React.useImperativeHandle(forwardedRef, () => listBoxRef.current as HTMLUListElement);

    // Get items to render based on grouping and drag and drop state
    const itemsToRender = React.useMemo(() => {
      const result = (isAllowDnD && !isGroup && reorderedItems.length > 0) ? reorderedItems : allItems;
      return result;
    }, [allItems, reorderedItems, isAllowDnD, isGroup]);

    // Extract HTML props that might conflict
    const { autoFocus: _, ...htmlProps } = otherProps;

    if (!sections || sections.length === 0) {
      return (
        <ul
          {...mergeProps(listBoxProps, htmlProps)}
          ref={listBoxRef}
          className={styles.columnsList}
        />
      );
    }

    return (
      <ul
        {...mergeProps(listBoxProps, htmlProps)}
        ref={listBoxRef}
        className={styles.columnsList}
      >
        {isGroup ? (
          // Render with sections
          sections.map((section) => (
            <React.Fragment key={section.id}>
              <li className={styles.section} role="presentation">
                <div className={styles.sectionTitle}>{section.label}</div>
              </li>
              {(section.items || []).map((item) => (
                <Option 
                  key={item.id} 
                  item={item} 
                  state={state} 
                  isIndeterminate={false}
                  onVisibilityChange={onVisibilityChange}
                  onFilterChange={onFilterChange}
                  isAllowDnD={isAllowDnD}
                  isGroup={isGroup}
                  isDragging={draggedKey === item.id}
                  isDragOver={dragOverKey === item.id}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragLeave={handleDragLeave}
                />
              ))}
            </React.Fragment>
          ))
        ) : (
          // Render flat list
          itemsToRender.map((item) => (
            <Option 
              key={item.id} 
              item={item} 
              state={state} 
              isIndeterminate={false}
              onVisibilityChange={onVisibilityChange}
              onFilterChange={onFilterChange}
              isAllowDnD={isAllowDnD}
              isGroup={isGroup}
              isDragging={draggedKey === item.id}
              isDragOver={dragOverKey === item.id}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragLeave={handleDragLeave}
            />
          ))
        )}
      </ul>
    );
  }
);

ColumnsList.displayName = 'ColumnsList';
